<script>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import dayjs from 'dayjs';
import { useRoute, useRouter } from "vue-router";
import { useDataStore } from "@/store/dataStore";
import orderService from "@/services/orderService";
import Swal from "sweetalert2";

const columns = [
  {
    title: "Tanggal",
    dataIndex: "tanggal_order",
    key: "tanggal",
    sorter: true,
  },
  {
    title: "Pemilik",
    dataIndex: "member_nama",
    key: "member_nama",
    sorter: { compare: (a, b) => (a.member_nama || "").localeCompare(b.member_nama || "") },
  },
  {
    title: "Nama Toko",
    dataIndex: "member_toko",
    key: "member_toko",
    sorter: { compare: (a, b) => (a.member_toko || "").localeCompare(b.member_toko || "") },
  },
  {
    title: "Realisasi Kirim",
    key: "realisasi",
    width: 160,
  },
  {
    title: "Status",
    key: "status_badge",
    width: 160,
  },
  {
    title: "Aksi",
    key: "action",
    width: 100,
  },
];

export default {
  setup() {
    const router = useRouter();
    const filter = ref(false);
    const searchQuery = ref("");
    const statusFilter = ref("");
    const fromDate = ref("");
    const toDate = ref("");
    const sortBy = ref("Terbaru");
    
    const route = useRoute();
    const { orders, state, refreshData, loadAllData } = useDataStore();
    const isRefreshing = ref(false);
    const loading = computed(() => (state.isLoading || isRefreshing.value) && orders.value.length === 0);

    // Sync status filter with route query
    onMounted(() => {
      if (route.query.status) {
        statusFilter.value = route.query.status;
      }
      
      // Sync date filter from calendar
      if (route.query.date) {
        fromDate.value = route.query.date;
        toDate.value = route.query.date;
      }
      
      if (state.isLoaded && orders.value.length === 0) {
        refreshData('orders');
      }
    });

    watch(() => route.query.status, (newStatus) => {
      statusFilter.value = newStatus || "";
    });

    // Watch for date changes from calendar navigation
    watch(() => route.query.date, (newDate) => {
      if (newDate) {
        fromDate.value = newDate;
        toDate.value = newDate;
      } else {
        fromDate.value = "";
        toDate.value = "";
      }
    });

    const getCalculatedStatus = (record) => {
      const shipped = record.shipped_qty || 0;
      const total = record.total_qty || 0;
      
      if (record.status === 'draft') return 'draft';
      if (shipped === 0) return 'processing';
      if (shipped < total) return 'partial';
      if (shipped >= total && total > 0) return 'completed';
      return 'processing';
    };

    const filteredOrders = computed(() => {
      let result = orders.value;
      
      // Filter by status (Real-time Shipment Status)
      if (statusFilter.value) {
        result = result.filter(o => getCalculatedStatus(o) === statusFilter.value);
      }

      // Filter by Date Range
      if (fromDate.value) {
        const start = new Date(fromDate.value).setHours(0,0,0,0);
        result = result.filter(o => new Date(o.tanggal_order).getTime() >= start);
      }
      if (toDate.value) {
        const end = new Date(toDate.value).setHours(23,59,59,999);
        result = result.filter(o => new Date(o.tanggal_order).getTime() <= end);
      }
      
      // Filter by search query
      if (searchQuery.value) {
        const q = searchQuery.value.toLowerCase();
        result = result.filter(
          (o) =>
            (o.member_toko && o.member_toko.toLowerCase().includes(q)) ||
            (o.member_nama && o.member_nama.toLowerCase().includes(q)) ||
            String(o.id).includes(q)
        );
      }

      // Sorting logic
      if (sortBy.value === "Terbaru") {
        result = [...result].sort((a, b) => new Date(b.tanggal_order) - new Date(a.tanggal_order));
      } else if (sortBy.value === "Terlama") {
        result = [...result].sort((a, b) => new Date(a.tanggal_order) - new Date(b.tanggal_order));
      }
      
      return result;
    });

    const orderStats = computed(() => {
      // Menggunakan orders.value (bukan filtered) agar summary card menunjukkan total keseluruhan
      const all = orders.value || [];
      const stats = {
        total: all.length,
        processing: 0,
        partial: 0,
        completed: 0
      };

      all.forEach(o => {
        const s = getCalculatedStatus(o);
        if (s === 'processing' || s === 'draft') stats.processing++;
        else if (s === 'partial') stats.partial++;
        else if (s === 'completed') stats.completed++;
      });

      return stats;
    });

    // Polling untuk data realtime (setiap 10 detik)
    let refreshInterval = null;

    onMounted(async () => {
      if (route.query.status) {
        statusFilter.value = route.query.status;
      }
      
      // Refresh awal
      isRefreshing.value = true;
      await refreshData('orders');
      isRefreshing.value = false;

      // Set interval polling lebih cepat (10 detik)
      refreshInterval = setInterval(() => {
        refreshData('orders');
      }, 10000); 
    });

    onUnmounted(() => {
      if (refreshInterval) clearInterval(refreshInterval);
    });

    const formatDate = (dateStr) => {
      if (!dateStr) return "-";
      return new Date(dateStr).toLocaleDateString("id-ID", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    };

    const getStatusInfo = (record) => {
      const calcStatus = getCalculatedStatus(record);
      
      switch(calcStatus) {
        case 'draft':
          return { text: 'Draft', class: 'bg-secondary text-white' };
        case 'processing':
          return { text: 'Processing', class: 'bg-black text-white' };
        case 'partial':
          return { text: 'Partial', class: 'bg-warning text-dark' };
        case 'completed':
          return { text: 'Completed', class: 'bg-success text-white' };
        default:
          return { text: 'Processing', class: 'bg-black text-white' };
      }
    };

    const getProgress = (record) => {
      const shipped = record.shipped_qty || 0;
      const total = record.total_qty || 0;
      if (total === 0) return 0;
      return Math.min(Math.round((shipped / total) * 100), 100);
    };

    const goToDetail = (id) => {
      router.push(`/purchases/order-details/${id}`);
    };

    const deleteOrder = (id) => {
      Swal.fire({
        title: "Hapus PO?",
        text: "Data yang dihapus tidak dapat dikembalikan!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Ya, Hapus!",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await orderService.deleteOrder(id);
            Swal.fire({
              toast: true,
              position: 'top-end',
              icon: 'success',
              title: 'PO berhasil dihapus',
              showConfirmButton: false,
              timer: 2000,
              timerProgressBar: true
            });
            refreshData('orders');
          } catch (e) {
            Swal.fire("Error", "Gagal menghapus PO", "error");
          }
        }
      });
    };

    const clearFilters = () => {
        statusFilter.value = "";
        fromDate.value = "";
        toDate.value = "";
        searchQuery.value = "";
    };

    const editModalVisible = ref(false);
    const editingOrder = ref(null);
    const editForm = ref({ tanggal_order: "" });

    const openEditModal = (order) => {
        editingOrder.value = order;
        editForm.value.tanggal_order = order.tanggal_order.split('T')[0];
        editModalVisible.value = true;
    };

    const handleUpdateOrder = async () => {
        if (!editingOrder.value) return;
        try {
            await orderService.updateOrder(editingOrder.value.id, {
                tanggal_order: new Date(editForm.value.tanggal_order).toISOString()
            });
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Tanggal PO diperbarui',
                showConfirmButton: false,
                timer: 2000
            });
            editModalVisible.value = false;
            refreshData('orders');
        } catch (e) {
            Swal.fire("Error", "Gagal memperbarui PO", "error");
        }
    };

    const formatDateShort = (dateStr) => {
      if (!dateStr) return "-";
      return new Date(dateStr).toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    const dateModalVisible = ref(false);
    const mobileSelectedDate = ref("");

    const openDateModal = () => {
        dateModalVisible.value = true;
    };

    const handleDateChange = (val) => {
        // Handle value from a-calendar (dayjs object)
        const dateStr = val.format('YYYY-MM-DD');
        mobileSelectedDate.value = dateStr;
        fromDate.value = dateStr;
        toDate.value = dateStr;
    };

    const confirmDateFilter = () => {
        if (!mobileSelectedDate.value) {
            // Default to today if nothing selected
            const today = dayjs().format('YYYY-MM-DD');
            mobileSelectedDate.value = today;
            fromDate.value = today;
            toDate.value = today;
        }
        dateModalVisible.value = false;
        refreshData('orders');
    };

    const clearDateFilter = () => {
        mobileSelectedDate.value = "";
        fromDate.value = "";
        toDate.value = "";
    };

    return {
      filter,
      loading,
      columns,
      orders,
      filteredOrders,
      searchQuery,
      statusFilter,
      fromDate,
      toDate,
      sortBy,
      formatDate,
      formatDateShort,
      getStatusInfo,
      getProgress,
      orderStats,
      goToDetail,
      deleteOrder,
      refreshData,
      clearFilters,
      dateModalVisible,
      mobileSelectedDate,
      openDateModal,
      handleDateChange,
      confirmDateFilter,
      clearDateFilter,
      dayjs,
      editModalVisible,
      editingOrder,
      editForm,
      openEditModal,
      handleUpdateOrder,
      router,
      Sortby: ["Urutkan Tanggal", "Terbaru", "Terlama"],
    };
  },
};
</script>

<style scoped>
.custom-date-input {
    border: 1px solid #d9d9d9 !important;
    box-shadow: none !important;
    height: 40px !important;
    display: flex;
    align-items: center;
}
.custom-date-input:hover, .custom-date-input:focus {
    border-color: #029147 !important;
}
:deep(.ant-picker-focused) {
    border-color: #029147 !important;
    box-shadow: 0 0 0 2px rgba(2, 145, 71, 0.2) !important;
}
:deep(.ant-picker-today-btn) {
    color: #029147 !important;
}
:deep(.ant-picker-active-bar) {
    background: #029147 !important;
}
:deep(.ant-btn-primary) {
    background-color: #029147 !important;
    border-color: #029147 !important;
}
.themed-datepicker .input-group-text {
    border-right: 0;
}
</style>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <!-- MOBILE VIEW (Native Style) -->
    <div class="content d-lg-none mob-order-list">
        <!-- Header & Search -->
        <div class="px-2 mb-4">
           <div class="d-flex justify-content-between align-items-center mb-3">
              <div class="d-flex align-items-center gap-3">
                 <a href="javascript:void(0);" @click="router.push('/dashboard')" class="btn-back-mob shadow-sm">
                    <vue-feather type="arrow-left" size="20"></vue-feather>
                 </a>
                 <h4 class="fw-bold mb-0">Daftar PO</h4>
              </div>
              <router-link to="/purchases/add-order" class="btn btn-success-circle shadow-sm">
                 <vue-feather type="plus" size="20"></vue-feather>
              </router-link>
           </div>
           
           <div class="search-input-mob shadow-xs">
              <vue-feather type="search" size="18" class="text-muted"></vue-feather>
              <input type="text" v-model="searchQuery" placeholder="Cari Kios..." class="border-0 bg-transparent w-100 ms-2" />
           </div>
        </div>

        <!-- Status Filter Chips (Scrollable) -->
        <div class="filter-scroll-wrapper mb-4">
           <div class="d-flex gap-2 px-2 pb-2">
              <button 
                 @click="statusFilter = ''" 
                 class="filter-chip" :class="{ active: statusFilter === '' }"
              >Semua</button>

              <!-- Date Filter Chip Mobile -->
              <button 
                 @click="openDateModal" 
                 class="filter-chip d-flex align-items-center gap-2" :class="{ active: mobileSelectedDate !== '' }"
              >
                 <vue-feather type="calendar" size="14"></vue-feather>
                 {{ mobileSelectedDate ? formatDateShort(mobileSelectedDate) : 'Kalender' }}
                 <span v-if="mobileSelectedDate" @click.stop="clearDateFilter" class="ms-1 times-circle">×</span>
              </button>

              <button 
                 @click="statusFilter = 'processing'" 
                 class="filter-chip" :class="{ active: statusFilter === 'processing' }"
              >Processing</button>
              <button 
                 @click="statusFilter = 'partial'" 
                 class="filter-chip" :class="{ active: statusFilter === 'partial' }"
              >Partial</button>
              <button 
                 @click="statusFilter = 'completed'" 
                 class="filter-chip" :class="{ active: statusFilter === 'completed' }"
              >Completed</button>
           </div>
        </div>

        <!-- Order List Cards -->
        <div class="px-2 pb-5">
           <div v-if="filteredOrders.length > 0">
              <div 
                v-for="order in filteredOrders" 
                :key="'mob-po-'+order.id" 
                class="mob-po-card mb-4 p-0 bg-white"
                @click="goToDetail(order.id)"
                style="cursor: pointer;"
              >
                 <!-- Card Header: Status & ID -->
                 <div class="px-3 pt-3 pb-2 d-flex justify-content-between align-items-center">
                    <span :class="['badge rounded-pill smallest px-2 py-1 uppercase', getStatusInfo(order).class]">
                       {{ getStatusInfo(order).text }}
                    </span>
                    <span class="smallest text-muted opacity-50 fw-bold">#{{ order.id }}</span>
                 </div>

                 <!-- Shop Info Section with Tint -->
                 <div class="px-3 pb-3 pt-1 bg-light-tint mx-2 rounded-4 mb-3">
                    <div class="d-flex justify-content-between align-items-start mb-2 pt-2">
                       <h6 class="fw-bold text-dark mb-0 flex-fill">{{ order.member_toko }}</h6>
                       <span class="smallest text-dark fw-bold ms-2">{{ formatDateShort(order.tanggal_order) }}</span>
                    </div>
                    <span class="smallest text-muted text-uppercase fw-bold">{{ order.member_nama }}</span>
                 </div>

                 <!-- Progress Content -->
                 <div class="px-3 pb-3">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                       <span class="smallest text-muted uppercase fw-bold">Realisasi Kirim</span>
                        <span class="smallest fw-bold text-success">{{ order.shipped_qty }} / {{ order.total_qty }} <span class="uppercase">{{ order.main_qty_type || 'Dus' }}</span></span>
                    </div>
                    <div class="progress" style="height: 6px; border-radius: 10px; background: #f0f0f0;">
                       <div class="progress-bar bg-success" :style="{ width: getProgress(order) + '%' }"></div>
                    </div>
                 </div>
              </div>
           </div>
           
           <!-- Empty State Mobile -->
           <div v-else class="text-center py-5">
              <div class="bg-light p-4 rounded-circle d-inline-block mb-3">
                 <vue-feather type="clipboard" class="text-muted" size="40"></vue-feather>
              </div>
              <p class="text-muted small">Tidak ada data PO ditemukan.</p>
           </div>
        </div>
    </div>

    <!-- Date Picker Modal Mobile (Inline Calendar) -->
    <a-modal v-model:visible="dateModalVisible" title="Filter Tanggal" :footer="null" centered width="360px">
        <div class="p-1">
            <div class="mob-custom-calendar-container">
               <a-calendar 
                  :fullscreen="false" 
                  @select="handleDateChange"
                  :value="mobileSelectedDate ? dayjs(mobileSelectedDate) : dayjs()"
               />
            </div>
            <div class="row g-2 mt-3">
               <div class="col-6">
                  <button @click="clearDateFilter(); dateModalVisible = false" class="btn btn-outline-secondary w-100 fw-bold smallest rounded-pill py-2">
                     RESET
                  </button>
               </div>
               <div class="col-6">
                  <button @click="confirmDateFilter" class="btn btn-success w-100 fw-bold smallest rounded-pill py-2 text-white">
                     FILTER
                  </button>
               </div>
            </div>
        </div>
    </a-modal>

    <!-- Edit Modal Mobile -->
    <a-modal v-model:visible="editModalVisible" title="Edit Informasi PO" @ok="handleUpdateOrder" centered okText="Update" cancelText="Batal">
        <div class="p-2">
            <div class="mb-3">
                <label class="form-label smallest fw-bold uppercase text-muted">Tanggal Order</label>
                <input type="date" v-model="editForm.tanggal_order" class="form-control rounded-3" />
            </div>
            <div v-if="editingOrder" class="p-3 bg-light rounded-3">
                <h6 class="fw-bold mb-1">{{ editingOrder.member_toko }}</h6>
                <p class="smallest text-muted mb-0">Hanya dapat mengubah tanggal pesanan melalui fitur cepat ini.</p>
            </div>
        </div>
    </a-modal>

    <!-- DESKTOP VIEW (Untouched) -->
    <div class="content d-none d-lg-block">
      <div class="page-header transfer">
        <div class="add-item d-flex">
          <div class="page-title">
            <h4>Daftar Purchase Order</h4>
            <h6>Kelola seluruh pesanan Kios SSP</h6>
          </div>
        </div>
        <ul class="table-top-head">
          <li>
            <a data-bs-toggle="tooltip" data-bs-placement="top" title="Pdf">
              <img src="@/assets/img/icons/pdf.svg" alt="img" />
            </a>
          </li>
          <li>
            <a data-bs-toggle="tooltip" data-bs-placement="top" title="Excel">
              <img src="@/assets/img/icons/excel.svg" alt="img" />
            </a>
          </li>
          <li>
            <a data-bs-toggle="tooltip" data-bs-placement="top" title="print">
              <img src="@/assets/img/icons/printer.svg" alt="img" />
            </a>
          </li>
          <li>
            <a
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Refresh"
              @click="refreshData('orders')"
            >
              <i data-feather="rotate-ccw" class="feather-rotate-ccw"></i>
            </a>
          </li>
          <li>
            <a
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Collapse"
              id="collapse-header"
            >
              <i data-feather="chevron-up" class="feather-chevron-up"></i>
            </a>
          </li>
        </ul>
        <div v-if="$hasPermission('ssp', 'orders', 'create')" class="d-flex purchase-pg-btn">
          <div class="page-btn">
            <router-link to="/purchases/add-order" class="btn btn-added">
              <vue-feather type="plus-circle" class="me-2"></vue-feather>Buat PO Baru
            </router-link>
          </div>
        </div>
      </div>

      <!-- Summary Cards - Only show on main list (no status filter) -->
      <div v-if="!statusFilter" class="row mb-4">
        <div class="col-xl-3 col-sm-6 col-12 d-flex">
          <div class="dash-count w-100">
            <div class="dash-counts">
              <h4>{{ orderStats.total }}</h4>
              <h5>Total PO</h5>
            </div>
            <div class="dash-imgs">
              <vue-feather type="shopping-cart" style="width: 60px; height: 60px;"></vue-feather>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-sm-6 col-12 d-flex">
          <div class="dash-count das2 w-100">
            <div class="dash-counts">
              <h4>{{ orderStats.processing }}</h4>
              <h5>PO Processing</h5>
            </div>
            <div class="dash-imgs">
              <vue-feather type="loader" style="width: 60px; height: 60px;"></vue-feather>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-sm-6 col-12 d-flex">
          <div class="dash-count w-100" style="background-color: #ff9f43;">
            <div class="dash-counts">
              <h4 class="text-white">{{ orderStats.partial }}</h4>
              <h5 class="text-white">PO Partial</h5>
            </div>
            <div class="dash-imgs">
              <vue-feather type="truck" style="width: 60px; height: 60px; color: white;"></vue-feather>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-sm-6 col-12 d-flex">
          <div class="dash-count w-100">
            <div class="dash-counts">
              <h4>{{ orderStats.completed }}</h4>
              <h5>PO Completed</h5>
            </div>
            <div class="dash-imgs">
              <vue-feather type="check-square" style="width: 60px; height: 60px;"></vue-feather>
            </div>
          </div>
        </div>
      </div>

      <!-- Table List Card -->
      <div class="card table-list-card">
        <div class="card-body">
          <div class="table-top">
            <div class="search-set">
              <div class="search-input">
                <input
                  type="text"
                  placeholder="Cari Kios atau ID PO..."
                  class="dark-input"
                  v-model="searchQuery"
                />
                <a href="javascript:void(0);" class="btn btn-searchset">
                  <i data-feather="search" class="feather-search"></i>
                </a>
              </div>
            </div>
            <div class="search-path">
              <a
                class="btn btn-filter"
                id="filter_search"
                v-on:click="filter = !filter"
                :class="{ setclose: filter }"
              >
                <vue-feather type="filter" class="filter-icon"></vue-feather>
                <span><img src="@/assets/img/icons/closes.svg" alt="img" /></span>
              </a>
            </div>
            <div class="form-sort">
              <vue-feather type="sliders" class="info-img"></vue-feather>
              <vue-select
                :options="Sortby"
                id="Sortby"
                v-model="sortBy"
                placeholder="Urutkan Tanggal"
              />
            </div>
          </div>

          <!-- /Filter -->
          <div
            class="card mb-0 shadow-none border-0 bg-light"
            id="filter_inputs"
            :style="{ display: filter ? 'block' : 'none' }"
          >
            <div class="card-body pb-0">
              <div class="row g-3 align-items-end mb-3">
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="input-blocks">
                    <label class="form-label small fw-bold text-muted">STATUS</label>
                    <div class="input-group">
                        <span class="input-group-text bg-white"><vue-feather type="stop-circle" style="width: 16px;"></vue-feather></span>
                        <select class="form-select border-start-0" v-model="statusFilter">
                            <option value="">Semua Status</option>
                            <option value="processing">Processing</option>
                            <option value="partial">Partial</option>
                            <option value="completed">Completed</option>
                        </select>
                    </div>
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="input-blocks">
                    <label class="form-label small fw-bold text-muted text-uppercase">Dari Tanggal</label>
                    <div class="input-group themed-datepicker">
                        <span class="input-group-text bg-white"><vue-feather type="calendar" style="width: 16px;"></vue-feather></span>
                        <a-date-picker 
                          v-model:value="fromDate" 
                          placeholder="Pilih Tanggal" 
                          class="form-control border-start-0 custom-date-input"
                          value-format="YYYY-MM-DD"
                        />
                    </div>
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="input-blocks">
                    <label class="form-label small fw-bold text-muted text-uppercase">Sampai Tanggal</label>
                    <div class="input-group themed-datepicker">
                        <span class="input-group-text bg-white"><vue-feather type="calendar" style="width: 16px;"></vue-feather></span>
                        <a-date-picker 
                          v-model:value="toDate" 
                          placeholder="Pilih Tanggal" 
                          class="form-control border-start-0 custom-date-input"
                          value-format="YYYY-MM-DD"
                        />
                    </div>
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12 ms-auto text-end">
                  <div class="input-blocks">
                    <label class="form-label small fw-bold text-muted d-block">&nbsp;</label>
                    <button class="btn btn-outline-secondary py-2 px-3" @click="clearFilters" title="Reset Filter">
                      <vue-feather type="refresh-cw" class="me-1" style="width: 14px;"></vue-feather> Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- /Filter -->

          <div class="table-responsive product-list">
            <a-table
              :columns="columns"
              :data-source="filteredOrders"
              :loading="loading"
              :pagination="{ pageSize: 10, showSizeChanger: false }"
            >
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'tanggal'">
                  {{ formatDate(record.tanggal_order) }}
                </template>
                <template v-else-if="column.key === 'member_nama'">
                  <span class="text-uppercase small">{{ record.member_nama }}</span>
                </template>
                <template v-else-if="column.key === 'member_toko'">
                  <span class="fw-bold text-uppercase small">{{ record.member_toko }}</span>
                </template>
                <template v-else-if="column.key === 'realisasi'">
                  <div style="width: 120px;">
                    <div class="badge bg-dark w-100 py-2 mb-2" style="font-size: 12px; border-radius: 6px;">
                        {{ record.shipped_qty }} / {{ record.total_qty }} <span class="text-uppercase">{{ record.main_qty_type || 'Dus' }}</span>
                    </div>
                    <div class="progress" style="height: 4px; border-radius: 10px;">
                        <div class="progress-bar bg-success" role="progressbar" :style="{ width: getProgress(record) + '%' }"></div>
                    </div>
                  </div>
                </template>
                <template v-else-if="column.key === 'status_badge'">
                  <div style="width: 120px;">
                    <span 
                        class="badge px-3 py-2 w-100" 
                        :class="getStatusInfo(record).class"
                        style="font-size: 12px; border-radius: 6px;"
                    >
                        {{ getStatusInfo(record).text }}
                    </span>
                  </div>
                </template>
                <template v-else-if="column.key === 'action'">
                  <td class="action-table-data">
                    <div class="edit-delete-action">
                      <a
                        class="me-2 p-2 bg-light rounded"
                        href="javascript:void(0);"
                        @click="goToDetail(record.id)"
                      >
                        <vue-feather type="eye" class="action-eye text-dark" style="width: 18px; height: 18px;"></vue-feather>
                      </a>
                      <a
                        class="p-2 bg-light-danger rounded ms-1"
                        href="javascript:void(0);"
                        @click="deleteOrder(record.id)"
                        v-if="$hasPermission('ssp', 'orders', 'delete')"
                      >
                        <vue-feather type="trash-2" class="text-danger" style="width: 18px; height: 18px;"></vue-feather>
                      </a>
                    </div>
                  </td>
                </template>
              </template>
            </a-table>
          </div>
        </div>
      </div>
    </div>
  </div>
  <purchase-list-modal></purchase-list-modal>
</template>

<style scoped>
/* Mobile PO List Styles */
.mob-order-list {
  padding-top: 15px !important;
  background: #fdfdfd;
}

.btn-back-mob {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1e293b;
  border: 1px solid #f1f5f9;
  transition: all 0.2s;
}
.btn-back-mob:active {
  transform: scale(0.9);
  background: #f8fafc;
}

.btn-success-circle {
  width: 44px;
  height: 44px;
  background: #029147;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.search-input-mob {
  background: white;
  padding: 12px 16px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  border: 1px solid #f1f5f9;
}

.filter-scroll-wrapper {
  overflow-x: auto;
  scrollbar-width: none;
}
.filter-scroll-wrapper::-webkit-scrollbar { display: none; }

.filter-chip {
  padding: 8px 20px;
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  white-space: nowrap;
  transition: all 0.2s;
}

.filter-chip.active {
  background: #029147;
  color: white;
  border-color: #029147;
  box-shadow: 0 4px 10px rgba(2, 145, 71, 0.2);
}

.times-circle {
  background: rgba(255,255,255,0.3);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.mob-custom-calendar-container :deep(.ant-picker-calendar-header) {
   padding: 0 0 10px 0 !important;
}
.mob-custom-calendar-container :deep(.ant-picker-calendar-full) {
   background: transparent !important;
}
.mob-custom-calendar-container :deep(.ant-picker-cell-inner) {
   border-radius: 8px !important;
}
.mob-custom-calendar-container :deep(.ant-picker-cell-selected .ant-picker-cell-inner) {
   background: #029147 !important;
}

.mob-po-card {
  background: white;
  border-radius: 24px;
  border: none !important;
  box-shadow: 0 10px 25px rgba(0,0,0,0.06);
  overflow: hidden;
  transition: all 0.2s ease;
}

.mob-po-card:active { 
  transform: translateY(2px) scale(0.98); 
  box-shadow: 0 5px 15px rgba(0,0,0,0.04);
}

.bg-light-tint { background: #f8fafc; }
.bg-light-soft { background: #f8fafc; }
.shadow-xs { box-shadow: 0 2px 8px rgba(0,0,0,0.02); }

.btn-light-circle {
  width: 32px;
  height: 32px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  padding: 0;
}

.uppercase { text-transform: uppercase; letter-spacing: 0.5px; }
.smallest { font-size: 11px; }

@media (max-width: 991.98px) {
  .page-header { display: none !important; }
}

.bg-black {
    background-color: #000 !important;
}
.table-list-card {
    border-radius: 15px;
}
.btn-added {
    background-color: #000;
    color: #fff;
    border-radius: 8px;
    font-weight: bold;
}
.btn-added:hover {
    background-color: #333;
    color: #fff;
}
</style>
